import moment from "moment";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { addDays } from "../../../../utils/flexible-program";
import {
  blockLanding,
  blockState,
  blockSummary,
  globalState,
  selectAll,
  selectNone,
  toggleBlock,
} from "../../../../utils/import-template-selection";
import ImportCheck from "./ImportCheck";

const pretty = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D");

/** The empty items of one block, one row per week. */
const groupByWeek = (items) => {
  const weeks = new Map();
  for (const item of items) {
    weeks.set(item.week, [...(weeks.get(item.week) || []), item]);
  }
  return [...weeks.values()];
};

/**
 * "Blocks to copy": select all, then one card per block. When a selected
 * block has empty weeks or workouts, an amber note says what to fix and is
 * the only place to deselect them (nothing imports until they are).
 */
const ImportBlocks = ({
  blocks,
  selection,
  onChange,
  landings,
  invalidByBlock,
  onDeselect,
  templateId,
  templateName,
  editorPath,
}) => {
  const { t } = useTranslation("coaching-portal");
  const overall = globalState(selection, blocks);
  const allInvalid = [...invalidByBlock.values()].flat();
  const selectedBlocks = blocks.filter(
    (block) => blockState(selection, block) !== "none"
  ).length;
  const blockName = (block) =>
    block.name ||
    t("flexible.importTemplate.blockFallback", { order: block.order });

  return (
    <>
      <div className="imp-sec-head">
        <h3>{t("flexible.importTemplate.blocksTitle")}</h3>
        <span className="imp-count">
          {t("flexible.importTemplate.blocksSelected", {
            selected: selectedBlocks,
            total: blocks.length,
          })}
        </span>
      </div>

      <div className="imp-blocks">
        {blocks.length > 1 && (
          <div
            className="imp-select-all"
            role="presentation"
            onClick={() =>
              onChange(overall === "all" ? selectNone() : selectAll(blocks))
            }
          >
            <ImportCheck
              state={overall}
              label={t("flexible.importTemplate.selectAll")}
              onToggle={() =>
                onChange(overall === "all" ? selectNone() : selectAll(blocks))
              }
            />
            <span>{t("flexible.importTemplate.selectAll")}</span>
            {allInvalid.length > 0 && (
              <button
                type="button"
                data-button-type="secondary"
                className="sm imp-push"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeselect(allInvalid);
                }}
              >
                {t("flexible.importTemplate.invalid.deselectEvery", {
                  count: allInvalid.length,
                })}
              </button>
            )}
          </div>
        )}

        {blocks.map((block) => {
          const state = blockState(selection, block);
          const on = state !== "none";
          const summary = blockSummary(block);
          const landing = blockLanding(landings, block.order);
          const invalid = invalidByBlock.get(block.order) || [];
          const toggle = () => onChange(toggleBlock(selection, block));

          return (
            <div key={block.order} className={`imp-block ${on ? "on" : "off"}`}>
              <div
                className="imp-block-top"
                role="presentation"
                onClick={toggle}
              >
                <ImportCheck
                  state={state}
                  label={blockName(block)}
                  onToggle={toggle}
                />
                <div className="imp-block-main">
                  <div className="imp-block-name">
                    <b>{blockName(block)}</b>
                    {block.name && (
                      <span>
                        {t("flexible.importTemplate.blockNumber", {
                          order: block.order,
                        })}
                      </span>
                    )}
                  </div>
                  <div className="imp-block-land">
                    {landing
                      ? t("flexible.importTemplate.lands", {
                          weeks:
                            landing.first.programWeek ===
                            landing.last.programWeek
                              ? `${landing.first.programWeek}`
                              : `${landing.first.programWeek}–${landing.last.programWeek}`,
                          start: pretty(landing.first.startDate),
                          end: pretty(addDays(landing.last.startDate, 6)),
                        })
                      : t("flexible.importTemplate.notImported")}
                  </div>
                </div>
                <span className="imp-block-meta">
                  {t("flexible.importTemplate.blockCount", {
                    count: summary.weeks,
                  })}
                  {" · "}
                  {t("flexible.importTemplate.workoutsCount", {
                    count: summary.workouts,
                  })}
                  <br />
                  <span className="imp-muted">
                    {t("flexible.importTemplate.setsCount", {
                      count: summary.sets,
                    })}
                  </span>
                </span>
              </div>

              {invalid.length > 0 && (
                <div className="imp-note" role="alert">
                  <img src="/img/new-icons/chip-warning.svg" alt="" />
                  <div className="imp-note-main">
                    <div className="imp-note-title">
                      <span>
                        {t("flexible.importTemplate.invalid.title", {
                          count: invalid.length,
                        })}
                      </span>
                    </div>
                    <ul>
                      {groupByWeek(invalid).map((items) => {
                        const emptyWorkouts = items.filter(
                          (item) => item.reason === "emptyWorkout"
                        );
                        return (
                          <li key={items[0].week}>
                            <span>
                              <b>
                                {t("flexible.importTemplate.weekLabel", {
                                  week: items[0].week,
                                })}
                              </b>
                              {" · "}
                              {emptyWorkouts.length
                                ? t(
                                    "flexible.importTemplate.invalid.workoutsNoExercises",
                                    {
                                      count: emptyWorkouts.length,
                                      workouts: emptyWorkouts
                                        .map(
                                          (item) =>
                                            item.sessionName ||
                                            t(
                                              "flexible.importTemplate.workoutFallback",
                                              { order: item.sessionOrder }
                                            )
                                        )
                                        .join(", "),
                                    }
                                  )
                                : t(
                                    "flexible.importTemplate.invalid.noWorkouts"
                                  )}
                            </span>
                            <button
                              type="button"
                              data-button-type="tertiary"
                              className="sm"
                              onClick={() => onDeselect(items)}
                            >
                              {t("flexible.importTemplate.invalid.deselect")}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="imp-note-how">
                      {t("flexible.importTemplate.invalid.how", {
                        template: templateName,
                        block: blockName(block),
                      })}
                    </p>
                    <div className="imp-note-actions">
                      <a className="imp-linkbtn" href={editorPath(templateId, block.order)}>
                        <SvgIcon
                          src="/img/new-icons/export.svg"
                          useCurrentColor
                          classNames="imp-linkicon"
                        />
                        {t("flexible.importTemplate.invalid.openBlock", {
                          block: blockName(block),
                        })}
                      </a>
                      <button
                        type="button"
                        data-button-type="secondary"
                        className="sm"
                        onClick={() => onDeselect(invalid)}
                      >
                        {t("flexible.importTemplate.invalid.deselectAll")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ImportBlocks;
